// Convert HSL to hex
export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Convert hex to HSL
export function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

// Convert hex to RGB
export function hexToRgb(hex) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

// Get relative luminance for contrast
export function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export function getTextColor(bgHex) {
  return getLuminance(bgHex) > 0.35 ? '#1a1a1a' : '#f5f0e8';
}

// Generate colour harmony relationships
export function getHarmonies(hex) {
  const { h, s, l } = hexToHsl(hex);

  // Helper: clamp lightness to a visible painting range
  const pl = (base, offset) => Math.min(85, Math.max(15, base + offset));

  return {
    base: {
      label: 'Base',
      description: 'Your chosen colour',
      swatches: [
        { hex, label: 'Shadow', h, s: Math.min(s + 10, 100), l: pl(l, -25) },
        { hex, label: 'Midtone', h, s, l },
        { hex, label: 'Highlight', h, s: Math.max(s - 15, 10), l: pl(l, +22) },
      ].map(sw => ({ ...sw, hex: hslToHex(sw.h, sw.s, sw.l) })),
    },
    complementary: {
      label: 'Complementary',
      description: 'Directly opposite on the wheel. Maximum contrast - use sparingly for focal points like glowing eyes or gemstones. Follow the 80/20 rule: 80% base, 20% complement.',
      swatches: (() => {
        const ch = (h + 180) % 360;
        return [
          { label: 'Shadow', hex: hslToHex(ch, Math.min(s + 10, 100), pl(l, -25)) },
          { label: 'Midtone', hex: hslToHex(ch, s, l) },
          { label: 'Highlight', hex: hslToHex(ch, Math.max(s - 15, 10), pl(l, +22)) },
        ];
      })(),
    },
    splitComplementary: {
      label: 'Split-Complementary',
      description: 'Two colours flanking the complement at 150 and 210 degrees. Softer contrast than direct complementary - great for secondary armour trim or cloth where you want interest without fighting the focal point.',
      swatches: (() => {
        const h1 = (h + 150) % 360;
        const h2 = (h + 210) % 360;
        return [
          { label: 'Split A', hex: hslToHex(h1, s, l) },
          { label: 'Split A Light', hex: hslToHex(h1, Math.max(s - 15, 10), pl(l, +20)) },
          { label: 'Split B', hex: hslToHex(h2, s, l) },
          { label: 'Split B Light', hex: hslToHex(h2, Math.max(s - 15, 10), pl(l, +20)) },
        ];
      })(),
    },
    analogous: {
      label: 'Analogous',
      description: 'Adjacent hues that sit naturally together. Ideal for smooth blending, NMM, OSL, and organic textures like skin or leather. Vary your lights and darks to avoid blandness.',
      swatches: (() => {
        return [-60, -30, 0, 30, 60].map((offset) => ({
          label: offset === 0 ? 'Base' : `${offset > 0 ? '+' : ''}${offset}°`,
          hex: hslToHex((h + offset + 360) % 360, s, l),
        }));
      })(),
    },
    triadic: {
      label: 'Triadic',
      description: 'Three hues evenly spaced at 120 degrees. Vibrant and balanced - assign one dominant colour, one secondary, one accent. Works well for multi-faction armies and heraldic schemes.',
      swatches: [0, 120, 240].map((offset) => ({
        label: offset === 0 ? 'Base' : `+${offset}°`,
        hex: hslToHex((h + offset) % 360, s, l),
      })),
    },
    tetradic: {
      label: 'Tetradic',
      description: 'Four colours forming two complementary pairs. Rich but demanding - one colour must dominate or the eye has nowhere to rest. Best for warbands or display pieces with plenty of surface area.',
      swatches: [0, 90, 180, 270].map((offset) => ({
        label: offset === 0 ? 'Base' : `+${offset}°`,
        hex: hslToHex((h + offset) % 360, s, l),
      })),
    },
    neutrals: {
      label: 'Neutrals & Washes',
      description: 'Desaturated tones pulled from your base hue - for washes, basing, and NMM. Using neutrals that share your base hue keeps the whole model feeling cohesive.',
      swatches: [
        { label: 'Dark Wash', hex: hslToHex(h, Math.min(s + 5, 30), 10) },
        { label: 'Mid Wash', hex: hslToHex(h, Math.min(s, 25), 25) },
        { label: 'Stone', hex: hslToHex(h, Math.max(s - 40, 5), 55) },
        { label: 'Pale', hex: hslToHex(h, Math.max(s - 50, 3), 80) },
        { label: 'Off-White', hex: hslToHex(h, Math.max(s - 55, 2), 92) },
      ],
    },
  };
}

// Find closest named paint (simplified lookup by hue range)
export function getHueLabel(h) {
  if (h < 15 || h >= 345) return 'Red';
  if (h < 30) return 'Red-Orange';
  if (h < 50) return 'Orange';
  if (h < 65) return 'Yellow-Orange';
  if (h < 80) return 'Yellow';
  if (h < 105) return 'Yellow-Green';
  if (h < 140) return 'Green';
  if (h < 165) return 'Teal-Green';
  if (h < 195) return 'Cyan';
  if (h < 225) return 'Sky Blue';
  if (h < 255) return 'Blue';
  if (h < 275) return 'Blue-Violet';
  if (h < 295) return 'Violet';
  if (h < 315) return 'Purple';
  if (h < 330) return 'Magenta';
  return 'Rose';
}
